<template>
  <div class="echarts-option-settings">
    <template v-if="['line', 'bar', 'pie'].includes(kind)">
      <el-form-item label="持续动效"><el-switch :value="!!(node.option && node.option.chartMotion)" :disabled="disabled" aria-label="图表持续动效" @input="setMotion('chartMotion', $event)" /></el-form-item>
      <el-form-item v-if="node.option && node.option.chartMotion" label="动效周期（秒）"><el-input-number :value="node.option.chartMotionSeconds || 6" :min="2" :max="20" :precision="0" :disabled="disabled" aria-label="图表动效周期" @change="setMotion('chartMotionSeconds', $event)" /></el-form-item>
      <p v-if="node.option && node.option.chartMotion" class="echarts-option-settings__hint">分类轴图表横向向左、纵向向上循环播放已有样本，没有新数据也会推进；悬停查看原始数据。圆环持续流光，其他真实统计读数不参与模拟。后台与减少动态效果模式暂停。</p>
    </template>
    <el-form-item label="ECharts Option">
      <el-button ref="trigger" size="mini" :disabled="disabled" @click="open">编辑配置</el-button>
      <span v-if="configured" class="echarts-option-settings__badge">已配置</span>
    </el-form-item>
    <p class="echarts-option-settings__hint">高级配置优先于普通样式；恢复默认可回到普通设置。</p>
    <el-dialog ref="dialog" title="编辑 ECharts Option" :visible.sync="visible" append-to-body custom-class="echarts-option-dialog" width="1080px" :close-on-click-modal="false" @opened="initEditor" @closed="disposeEditor" @keydown.tab.native="trapFocus">
      <p id="echarts-option-help" class="echarts-option-dialog__help">支持粘贴 { ... } 或 option = { ... };，可用单引号、注释和末尾逗号。不执行函数、脚本或外部资源。</p>
      <div class="echarts-option-dialog__layout">
        <section class="echarts-option-dialog__source" aria-label="Option 配置代码">
          <label for="echarts-option-source">配置内容</label>
          <textarea id="echarts-option-source" ref="source" v-model="draft" aria-describedby="echarts-option-help"></textarea>
        </section>
        <section class="echarts-option-dialog__preview" aria-label="图表静态预览">
          <h3>静态预览</h3>
          <div ref="previewArea" class="echarts-option-dialog__chart">
            <component v-if="preview" :is="'avue-echart-' + kind" v-bind="preview" :width="previewWidth" :height="300" :animation="false" :disabled="false" @echarts-option-error="previewFailed" @echarts-option-rendered="previewRendered" />
            <span v-else>点击“校验并预览”查看效果</span>
          </div>
          <p>{{ dynamic ? '动态模式：此处只看静态效果。运行时系列、坐标轴类型和数据使用当前数据源；样式按系列顺序套用。' : '静态模式：Option 中的系列与数据优先；未填写数据时沿用组件当前数据。' }}</p>
          <details v-if="lastValid"><summary>查看已校验配置（文本）</summary><pre>{{ JSON.stringify(lastValid, null, 2) }}</pre></details>
        </section>
      </div>
      <p v-if="error" class="echarts-option-dialog__error" role="alert">{{ error }} 输入已保留，预览保持上一份有效配置。</p>
      <p v-else class="echarts-option-dialog__status" role="status" aria-live="polite">{{ status }}</p>
      <span slot="footer" class="echarts-option-dialog__actions">
        <el-button size="small" :disabled="disabled || busy" @click="reset">恢复默认</el-button>
        <span class="echarts-option-dialog__actions-right">
          <el-button size="small" @click="visible = false">取消</el-button>
          <el-button size="small" :disabled="busy" @click="validate">校验并预览</el-button>
          <el-button size="small" type="primary" :disabled="disabled || busy" @click="apply">应用配置</el-button>
        </span>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import CodeMirror from 'codemirror/lib/codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/blackboard.css'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/addon/edit/matchbrackets'
import { parseEchartsOption } from '@/editor/core/echarts-option'
import { templatePreviewNodes } from '@/runtime/template-preview'

export default {
  props: { node: { type: Object, required: true }, disabled: Boolean },
  data: () => ({ visible: false, draft: '', error: '', status: '', preview: null, previewWidth: 400, lastValid: null, busy: false }),
  computed: {
    kind () { return this.node.component.name },
    configured () { return this.node.option && this.node.option.echartsOption !== undefined },
    dynamic () { return Number(this.node.dataType || 0) !== 0 }
  },
  beforeDestroy () { this.disposeEditor() },
  methods: {
    setMotion (key, value) { if (!this.node.option) this.$set(this.node, 'option', {}); this.$set(this.node.option, key, value) },
    open () {
      this.draft = JSON.stringify(this.configured ? this.node.option.echartsOption : {}, null, 2)
      this.error = ''; this.status = '配置仅在应用后写入画布；取消不会修改组件。'; this.preview = null; this.lastValid = null
      this.visible = true
    },
    trapFocus (event) {
      if (!this.$refs.dialog) return
      const controls = Array.from(this.$refs.dialog.$el.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]')).filter(el => el.getClientRects().length)
      const first = controls[0], last = controls[controls.length - 1]
      if (!first) return
      if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus() }
    },
    initEditor () {
      this.editor = CodeMirror.fromTextArea(this.$refs.source, { mode: 'text/javascript', theme: 'blackboard', lineNumbers: true, matchBrackets: true, tabSize: 2, lineWrapping: true, extraKeys: { Tab: false, 'Shift-Tab': false, 'Ctrl-Enter': () => this.validate() } })
      this.editor.setValue(this.draft)
      this.editor.getWrapperElement().setAttribute('aria-label', 'ECharts Option 配置代码')
      this.editor.getInputField().setAttribute('aria-label', 'ECharts Option 配置代码')
      this.editor.getInputField().setAttribute('aria-describedby', 'echarts-option-help')
      this.editor.on('change', () => { this.draft = this.editor.getValue(); this.error = ''; this.status = '内容已修改，请重新校验；尚未应用到画布。' })
      this.measure = () => { this.previewWidth = this.$refs.previewArea ? Math.max(1, this.$refs.previewArea.clientWidth) : 400 }
      this.measure()
      if (window.ResizeObserver) { this.observer = new window.ResizeObserver(this.measure); this.observer.observe(this.$refs.previewArea) }
      else window.addEventListener('resize', this.measure)
      this.editor.focus()
      this.validate()
    },
    disposeEditor () {
      if (this.finishPreview) this.finishPreview(false)
      if (this.editor) { this.draft = this.editor.getValue(); this.editor.toTextArea(); this.editor = null }
      if (this.observer) { this.observer.disconnect(); this.observer = null }
      if (this.measure) window.removeEventListener('resize', this.measure)
      this.preview = null
      if (!this._isBeingDestroyed && this.$refs.trigger) this.$nextTick(() => { if (this.$refs.trigger) this.$refs.trigger.$el.focus() })
    },
    previewFailed (error) {
      this.error = '图表无法绘制：' + (error.message || '请检查配置')
      if (this.finishPreview) this.finishPreview(false)
    },
    previewRendered () { if (this.finishPreview) this.finishPreview(true) },
    async validate () {
      if (this.busy) return false
      this.busy = true; this.error = ''
      const previous = this.preview
      try {
        const text = this.editor ? this.editor.getValue() : this.draft
        const parsed = parseEchartsOption(text, this.kind)
        const node = templatePreviewNodes([this.node])[0]
        const rendered = new Promise(resolve => {
          const timer = setTimeout(() => { this.error = '预览尚未就绪，请重试校验'; this.finishPreview(false) }, 2000)
          this.finishPreview = ok => { clearTimeout(timer); this.finishPreview = null; resolve(ok) }
        })
        // 预览只使用静态副本；禁用原组件遗留的处理函数和请求参数。
        this.preview = { ...node, option: { ...node.option, echartsOption: parsed, animation: false }, animation: false }
        if (!await rendered) { if (this.visible && !this._isBeingDestroyed) this.preview = previous; return false }
        if (!this.visible || text !== (this.editor ? this.editor.getValue() : this.draft)) { this.status = '内容已修改，请重新校验。'; return false }
        this.lastValid = parsed
        this.status = '校验通过，预览未写入画布。点击“应用配置”后，再按原流程保存。'
        return true
      } catch (error) { this.error = error.message; return false } finally { this.busy = false }
    },
    async apply () {
      if (this.disabled || !await this.validate() || !this.visible || this.disabled) return
      if (!this.node.option) this.$set(this.node, 'option', {})
      if (Object.keys(this.lastValid).length) this.$set(this.node.option, 'echartsOption', JSON.parse(JSON.stringify(this.lastValid)))
      else this.$delete(this.node.option, 'echartsOption')
      this.visible = false
    },
    reset () {
      if (this.disabled) return
      this.draft = '{}'
      if (this.editor) this.editor.setValue(this.draft)
      this.status = '已重置编辑内容，应用后恢复普通属性设置。'
      this.validate()
    }
  }
}
</script>
<style>
.echarts-option-settings__badge { margin-left: 8px; color: #66b1ff; font-size: 12px; }
.echarts-option-settings__hint { color: #9aaaba; font-size: 12px; line-height: 1.6; margin: -4px 0 12px; }
.echarts-option-dialog { max-width: calc(100vw - 32px); max-height: 88vh; margin-top: 6vh !important; display: flex; flex-direction: column; background: #202120; color: #e2e6e2; border: 1px solid #353835; }
.echarts-option-dialog .el-dialog__header, .echarts-option-dialog .el-dialog__footer { flex-shrink: 0; }
.echarts-option-dialog .el-dialog__body { max-height: 70vh; overflow-y: auto; min-height: 0; padding-top: 8px; color: #e2e6e2; }
.echarts-option-dialog .el-button--primary { background: #1680ff; border-color: #1680ff; color: #fff; }
.echarts-option-dialog__help { margin: 0 0 16px; color: #b5c4d0; line-height: 1.7; }
.echarts-option-dialog__layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; }
.echarts-option-dialog__source label, .echarts-option-dialog__preview h3 { display: block; margin: 0 0 8px; font-size: 14px; font-weight: 500; }
.echarts-option-dialog .CodeMirror { height: 350px; font-size: 13px; border: 1px solid #365062; }
.echarts-option-dialog__source textarea { width: 100%; height: 350px; box-sizing: border-box; }
.echarts-option-dialog__chart { height: 300px; background: #1e1f1e; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.echarts-option-dialog__preview p, .echarts-option-dialog__preview details { font-size: 12px; color: #b5c4d0; line-height: 1.7; margin-top: 10px; }
.echarts-option-dialog__preview pre { max-height: 140px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; }
.echarts-option-dialog__error, .echarts-option-dialog__status { margin: 12px 0 0; font-size: 12px; line-height: 1.7; }
.echarts-option-dialog__error { color: #f9a2a2; }
.echarts-option-dialog__status { color: #a9c2d0; }
.echarts-option-dialog__actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.echarts-option-dialog__actions-right { display: flex; gap: 8px; }
.echarts-option-dialog__actions .el-button + .el-button { margin-left: 0; }
@media (max-width: 760px) {
  .echarts-option-dialog__layout { grid-template-columns: minmax(0, 1fr); }
  .echarts-option-dialog .CodeMirror { height: 260px; }
  .echarts-option-dialog__actions { flex-wrap: wrap; }
}
</style>
